import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  getAuthenticityOptions,
  createAuthenticityOption,
  updateAuthenticityOption,
  deleteAuthenticityOption,
} from "../../api/adminService";

const EMPTY_FORM = { value: "", valueArabic: "", valueHebrew: "", displayOrder: "0" };

export default function AuthenticityOptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [dialogOption, setDialogOption] = useState(null); // { option } for edit, {option: null} for create, null closed
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  function loadOptions() {
    setLoading(true);
    getAuthenticityOptions()
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load authenticity options. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadOptions();
  }, []);

  async function handleSave(values) {
    const payload = {
      value: values.value.trim(),
      valueArabic: values.valueArabic.trim() || null,
      valueHebrew: values.valueHebrew.trim() || null,
      displayOrder: Number(values.displayOrder) || 0,
    };

    if (dialogOption.option) {
      const updated = await updateAuthenticityOption(dialogOption.option.authenticityOptionId, payload);
      setRows((prev) =>
        prev
          .map((r) => (r.authenticityOptionId === updated.authenticityOptionId ? updated : r))
          .sort((a, b) => a.displayOrder - b.displayOrder),
      );
      setSuccessMessage(`Updated ${updated.value}.`);
    } else {
      await createAuthenticityOption(payload);
      setSuccessMessage(`Created ${payload.value}.`);
      loadOptions();
    }
    setDialogOption(null);
  }

  async function confirmDelete() {
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteAuthenticityOption(deleteTarget.authenticityOptionId);
      setRows((prev) => prev.filter((r) => r.authenticityOptionId !== deleteTarget.authenticityOptionId));
      setSuccessMessage(`Deleted ${deleteTarget.value}.`);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.response?.data || "Could not delete this option.");
    } finally {
      setDeleting(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: "value", headerName: "Value", flex: 1, minWidth: 160 },
      { field: "valueArabic", headerName: "Value (Arabic)", flex: 1, minWidth: 160, valueGetter: (v) => v || "-" },
      { field: "valueHebrew", headerName: "Value (Hebrew)", flex: 1, minWidth: 160, valueGetter: (v) => v || "-" },
      { field: "displayOrder", headerName: "Order", width: 90 },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditOutlinedIcon fontSize="small" />}
            label="Edit"
            onClick={() => setDialogOption({ option: params.row })}
          />,
          <GridActionsCellItem
            icon={<DeleteOutlineRoundedIcon fontSize="small" />}
            label="Delete"
            onClick={() => {
              setDeleteTarget(params.row);
              setDeleteError("");
            }}
          />,
        ],
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <VerifiedOutlinedIcon sx={{ color: "primary.main" }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Authenticity Options
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage the authenticity values business owners can choose for a product (e.g. Original, Master, Turkey).
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={() => setDialogOption({ option: null })}
          sx={{ borderRadius: 2.5, px: 2.5, py: 1 }}
        >
          Add Option
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.authenticityOptionId}
          columns={columns}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
        />
      </Paper>

      {dialogOption && (
        <AuthenticityOptionDialog
          option={dialogOption.option}
          onClose={() => setDialogOption(null)}
          onSave={handleSave}
        />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>Delete "{deleteTarget.value}"?</DialogTitle>
          <DialogContent>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {typeof deleteError === "string" ? deleteError : "Could not delete this option."}
              </Alert>
            )}
            <Typography>
              You're about to delete <strong>{deleteTarget.value}</strong>. Deletion is blocked if any product is
              still using it.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
    </Box>
  );
}

function AuthenticityOptionDialog({ option, onClose, onSave }) {
  const [values, setValues] = useState(
    option
      ? {
          value: option.value || "",
          valueArabic: option.valueArabic || "",
          valueHebrew: option.valueHebrew || "",
          displayOrder: String(option.displayOrder ?? 0),
        }
      : EMPTY_FORM,
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  function setField(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  const isValid = values.value.trim().length > 0;

  async function submit() {
    setSaving(true);
    setError("");
    try {
      await onSave(values);
    } catch (err) {
      setError(err.response?.data || "Could not save this option.");
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {option ? "Edit Option" : "Add Option"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {option ? "Update this authenticity option." : "Create a new authenticity option."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>}

          <TextField
            label="Value"
            value={values.value}
            onChange={(e) => setField("value", e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Value (Arabic)"
            value={values.valueArabic}
            onChange={(e) => setField("valueArabic", e.target.value)}
            fullWidth
            dir="rtl"
          />
          <TextField
            label="Value (Hebrew)"
            value={values.valueHebrew}
            onChange={(e) => setField("valueHebrew", e.target.value)}
            fullWidth
            dir="rtl"
          />
          <TextField
            label="Display Order"
            type="number"
            value={values.displayOrder}
            onChange={(e) => setField("displayOrder", e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!isValid || saving}
          onClick={submit}
          sx={{ borderRadius: 2.5, px: 3 }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
